import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ProfessionalProfileBanner.css';

const ProfessionalProfileBanner = ({ onProfileEnabled }) => {
  const [showModal, setShowModal] = useState(false);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfessionalTypes();
    fetchLocations();
  }, []);

  const fetchProfessionalTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/professional-types/public`
      );
      setProfessionalTypes(response.data.professionalTypes || []);
    } catch (error) {
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/shop/my-shops`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userShops = Array.isArray(response.data) ? response.data : (response.data.shops || []);
      setLocations(userShops);
      // Auto-select if only one location
      if (userShops.length === 1) {
        setSelectedLocation(userShops[0]._id);
      }
    } catch (error) {
    }
  };

  const handleEnableProfessionalProfile = async () => {
    if (!selectedType) {
      setError('Please select a professional type');
      return;
    }

    if (!selectedLocation) {
      setError('Please select a location');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/owners/enable-professional-profile`,
        { 
          professionalType: selectedType,
          locationId: selectedLocation
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update token with new roles
      if (response.data.token) {
        localStorage.setItem('ogestyle_token', response.data.token);
        localStorage.setItem('ogestyle_roles', JSON.stringify(response.data.user.roles));
      }

      alert('Professional profile enabled successfully! You can now switch to your professional dashboard.');
      setShowModal(false);
      
      if (onProfileEnabled) {
        onProfileEnabled();
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to enable professional profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="professional-profile-banner">
        <div className="banner-content">
          <div className="banner-icon">✂️</div>
          <div className="banner-text">
            <h3>Work at Your Business Too?</h3>
            <p>Enable your professional profile to offer services and manage your own appointments.</p>
          </div>
          <button className="banner-btn" onClick={() => setShowModal(true)}>
            Enable Professional Profile
          </button>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content-pro" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Enable Professional Profile</h2>
            <p className="modal-subtitle">
              Select your professional type and location where you'll work.
            </p>

            {error && <div className="error-message">{error}</div>}

            <div className="form-section">
              <label className="form-label">Select Location</label>
              <select
                className="location-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">Choose a location...</option>
                {locations.map((loc) => (
                  <option key={loc._id} value={loc._id}>
                    {loc.name} - {loc.city}, {loc.state}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-section">
              <label className="form-label">Select Professional Type</label>
              <div className="professional-types-grid">
                {professionalTypes.map((type) => (
                  <div
                    key={type.value}
                    className={`professional-type-option ${selectedType === type.value ? 'selected' : ''}`}
                    onClick={() => setSelectedType(type.value)}
                  >
                    <div className="type-icon">{type.icon}</div>
                    <div className="type-label">{type.label}</div>
                    {type.description && (
                      <div className="type-description">{type.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleEnableProfessionalProfile}
                disabled={loading || !selectedType || !selectedLocation}
              >
                {loading ? 'Enabling...' : 'Enable Profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfessionalProfileBanner;
